import type { HeaderKind, LineKind } from "./draft.ts";
import { KEYS, keysLabel } from "./keymap.ts";
import type { DecisionKind, SectionKey } from "./types.ts";

/**
 * Lo que se lee en la pantalla de digitación y NO manda el API. Los nombres de
 * estados, pasos, motivos y tipos de foto llegan del back y no se traducen
 * aquí.
 */

export const SECTION_LABELS: Record<SectionKey, string> = {
  documentData: "Datos de la factura",
  items: "Líneas",
};

export const HEADER_LABELS: Record<HeaderKind, string> = {
  number: "Número",
  date: "Fecha",
};

export const LINE_LABELS: Record<LineKind, string> = {
  quantity: "Cantidad",
};

export const HEADER_PLACEHOLDERS: Record<HeaderKind, string> = {
  number: "No se leyó",
  date: "dd/mm/aaaa",
};

export const DECISION_LABELS: Record<DecisionKind, string> = {
  valid: "Válida",
  invalid: "Inválida",
  forData: "Para información",
};

/** La cola de facturas pendientes y lo que decide en el panel de datos. */
export const QUEUE_TEXTS = {
  panel: "Facturas por digitar",
  search: "Buscar factura",
  searchPlaceholder: "num:123 o #id",
  clearSearch: "Borrar la búsqueda",
  tabs: "Lo que propone el sistema",
  tabsHint: "← → cambian de pestaña",
  tabLabels: {
    "pre-aprobadas": "Pre-aprobadas",
    "pre-rechazadas": "Pre-rechazadas",
    "por-digitar": "Por digitar",
  },
  none: "Ninguna factura coincide con la búsqueda.",
  noneInTab: "No queda ninguna factura pendiente aquí.",
  documentOfTotal: (position: number, total: number, more: boolean) =>
    `Factura ${String(position)} de ${String(total)}${more ? " o más" : ""}`,
  number: (value: string) => `N.º ${value}`,
  claimedBy: (name: string) => `La está viendo ${name}`,
  /** Lo que decide en Conti Club Más, en el panel de datos. */
  checks: "Lo que decide",
  duplicateAlert: "Posible duplicada",
  byReviewer: "Decidido por el digitador",
  /** lala4 la devolvió por sus datos. */
  returnedTitle: "lala4 devolvió esta factura",
  returnedHint: "Corrige los datos y vuelve a decidir.",
} as const;

/** Los productos de cada línea: lo que dice el catálogo y lo que decide el digitador. */
export const PRODUCT_TEXTS = {
  /** Sin SKU: nadie supo cuál es. */
  unassigned: "Sin producto asignado",
  /** Quién lo dijo (la capa del reconocimiento). */
  layers: {
    exact: "Coincide con un producto conocido",
    similar: "Parecido a un producto conocido",
    service: "Parece un servicio",
    llm: "Lo sugirió la IA",
    family: "Familia participante",
    family_llm: "Familia sugerida por la IA",
    reviewer: "Decidido por el digitador",
    none: "Sin reconocer",
    assumed: "Asumido (no fue coincidencia exacta)",
  },
  confidence: {
    high: "Confianza alta",
    medium: "Confianza media",
    low: "Confianza baja",
  },
  byReviewer: "Decidido por el digitador",
  /** El texto con el que quedó guardado el alias del catálogo. */
  sizeMismatch: (sku: string, read: string) =>
    `La medida del SKU (${sku}) no coincide con la factura (${read})`,
  aliasSaved: (text: string) => `Alias guardado: «${text}»`,
  /**
   * Por qué no participa. Las claves (`otro_producto`, `otra_marca`,
   * `cemento`, `servicio`) vienen fijas del contrato actual (heredado de
   * ADELCA, ver `api-types.ts`); el texto de cada una sí es de este front.
   * `cemento` ya no nombra el producto ADELCA, queda con un texto genérico.
   * PENDIENTE (Conti): si el catálogo de llantas necesita sus propios
   * motivos de «no participa», hay que redefinir este enum junto al backend.
   */
  reasons: {
    otro_producto: "Es otro producto",
    otra_marca: "Es de otra marca",
    cemento: "No participa (otro producto)",
    servicio: "Es un servicio",
    not_a_tire: "No es llanta",
    other_brand: "Otra marca",
    rim_absent: "Sin rin",
  },
  /** Las dos formas de decir «no participa» desde el selector (con el teclado). */
  noneReasons: {
    not_a_tire: "No participa: no es llanta",
    other_brand: "No participa: otra marca",
  },
  change: "Cambiar producto",
  /** Línea de una familia participante con la medida por elegir. */
  familyPick: (family: string | null) =>
    `${family ?? "Familia participante"} · elige la medida`,
  familyOptions: "Medidas de la familia",
  /** El selector completo (id, código o nombre) desde una línea de familia. */
  more: "Más…",
  /** Lo que se dice al confirmar con una línea de familia sin SKU. */
  chooseSku: (number: number) => `Elige el SKU de la línea ${String(number)}`,
  noSizeLabel: "Sin medida",
  notParticipate: "No participa",
  /** En «No participan»: devolverla a un producto participante. */
  reclassify: "Es un producto participante",
  /** La sección plegada. */
  toResolveTitle: "Por resolver",
  participatingTitle: "Participan",
  othersTitle: "No participan",
  othersHint:
    "Líneas que no son productos participantes: se pueden reclasificar.",
  showOthers: (count: number) =>
    `Mostrar las ${String(count)} que no participan`,
  hideOthers: "Ocultar las que no participan",
  /** El selector de producto. */
  pickerLabel: "Buscar un producto participante",
  pickerPlaceholder: "Nombre, código o id del SKU…",
  /** El id (el SKU de lala4) y el código de cada producto, con lo que lo encontró. */
  productId: (id: number) => `Id del SKU en lala4: ${String(id)}`,
  productCode: (code: string) => `Código de material: ${code}`,
  productPoints: "Puntos por llanta",
  points: (points: number) => `${String(points)} pts`,
  /** Los puntos de la línea: cantidad × puntos por unidad. */
  linePoints: (line: number, unit: number) =>
    `${String(line)} pts (${String(unit)} por unidad)`,
  pickerHint: "Escribe el nombre, el código o el id del SKU para buscar.",
  pickerEmpty: "Ningún SKU coincide.",
  pickerLoading: "Buscando…",
  pickerFailed: "No se pudo buscar en el catálogo.",
  /** Al guardar. */
  assigned: (line: number, name: string | null) =>
    name === null
      ? `Línea ${String(line)}: producto asignado.`
      : `Línea ${String(line)}: ${name}.`,
  notParticipates: (line: number) => `Línea ${String(line)}: no participa.`,
  productBack: (line: number) => `Línea ${String(line)}: producto como estaba.`,
} as const;

export const MONTH_NAMES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
] as const;

export const TEXTS = {
  title: "Digitación",
  uploadMonth: "Mes de carga",
  uploadMonthHint:
    "Las facturas pendientes y la siguiente son de las subidas en este mes.",
  uploadMonthShown: (label: string) => `Mes de carga: ${label}`,
  work: "Work",
  batch: "Confirmar en lote",
  batchTitle: "Confirmar en lote",
  batchHint:
    "En orden de subida y con los datos tal como están. Entran las pre-aprobadas y las pre-rechazadas de confianza alta; las demás dicen por qué no. La decisión sigue siendo tuya: aquí solo confirmas lo que ya viste.",
  batchList: "Lo que se confirmaría",
  batchRowsHint: "recorren; la letra cambia el motivo:",
  batchRow: (id: string) => `Factura ${id}`,
  batchReason: (id: string) => `El motivo de la factura ${id}`,
  batchChanged: "cambiado",
  batchProposedWas: (label: string) => `Proponía: ${label}`,
  batchChangedCount: (count: number) =>
    count === 1
      ? "1 con el motivo cambiado."
      : `${String(count)} con el motivo cambiado.`,
  batchConfirm: (count: number) => `Confirmar ${String(count)}`,
  batchConfirmed: (count: number) =>
    count === 1
      ? "Se confirmó 1 factura."
      : `Se confirmaron ${String(count)} facturas.`,
  batchSkipped: (count: number) =>
    count === 1 ? "Se saltó 1:" : `Se saltaron ${String(count)}:`,
  batchSkipWhy: (label: string, message: string) => `${label}: ${message}`,
  batchDone: (count: number) =>
    count === 1
      ? "1 factura confirmada en lote."
      : `${String(count)} facturas confirmadas en lote.`,
  batchNothing: "No hay facturas pendientes para confirmar en lote.",
  done: "Listo",
  cancel: "Cancelar",
  loadMore: "Cargar más",
  takeNext: "Tomar siguiente",
  takeNextHint:
    "Toma la factura más antigua que espera digitación y nadie tiene tomada.",
  nothingOpen:
    "Elige una factura de la cola, o toma la siguiente, para empezar.",
  noneLeft: "No quedan facturas por digitar.",
  /** El nombre de un documento, por su identificador. */
  document: (id: string) => `Factura ${id}`,
  uploaded: "Subido",
  /** El title de la fecha de subida en la cola, con la fecha entera. */
  uploadedAt: (when: string) => `Subido ${when}`,
  participant: "Participante",
  participantId: "Identificación",
  participantKey: "ID participante",
  period: (label: string, source: "issued" | "uploaded") =>
    `${label} (${source === "issued" ? "por la fecha de la factura" : "por la fecha de subida"})`,
  photoFailed: "No se pudo cargar la foto.",
  /** Un PDF cuya imagen el back aún dibuja: llega con el refresco. */
  photoPreparing: "Preparando la imagen…",
  loading: "Cargando…",
  crop: "Recorte",
  fullPhoto: "Foto completa",
  noSection: "No vino esta sección: se ve la foto completa.",
  boxes: "Recuadros",
  boxesShown: "Recuadros visibles",
  boxesHidden: "Recuadros ocultos",
  data: "Los datos",
  lines: "Líneas",
  noLines: "No se leyó ninguna línea.",
  addedByHand: "Agregada a mano",
  suggested: "Sugerido",
  edited: "Corregido",
  /** El dato que la persona dio por bueno. */
  readValidated: "Lectura validada",
  lowConfidence: "Confianza baja",
  mediumConfidence: "Confianza media",
  /** Alt+A sin nada que mirar. */
  noAttention: "Nada más pide atención en esta factura.",
  /** Junto al dato al que apunta un chequeo que no quedó «Bien». */
  review: "Revisar",
  /** La sección con todos sus chequeos «Bien». */
  validated: "Validado",
  /** La sección con chequeos que piden mirar: «1 revisión», «2 revisiones». */
  reviews: (count: number) =>
    count === 1 ? "1 revisión" : `${String(count)} revisiones`,
  /** Para el lector: «Chequeos: Datos de la factura». */
  checksOf: "Chequeos",
  revert: (value: string) =>
    value === ""
      ? "Esc lo deja vacío, como se leyó"
      : `Esc vuelve a «${value}»`,
  notADate: "No es una fecha: escríbela como dd/mm/aaaa.",
  decision: "Decisión",
  reason: "Motivo",
  chooseReason: "Elige el motivo",
  save: "Guardar y seguir",
  /** La pre-revisión solo guía: confirmarla es decisión del digitador. */
  proposal: "Lo que propone el sistema",
  confirm: "Confirmar",
  willSave: (result: string) =>
    `Guarda «${result}» con los datos tal como están y pasa al siguiente.`,
  claimedByMe: (until: string) => `Tomado por ti hasta las ${until}`,
  claimedByOther: (by: string) => `Lo está viendo ${by}`,
  unclaimed: "Sin tomar: se toma al guardar",
  readOnly: "Solo lectura",
  decided: (label: string, by: string) => `Decidido: ${label}, por ${by}`,
  needDecision:
    "Elige la decisión: 1 Válida · 2 Inválida · 3 Para información.",
  needReason: "Elige el motivo de la inválida.",
  needNote: (reason: string) =>
    `«${reason}» pide una nota: escríbela y pulsa Enter.`,
  note: "Nota",
  notePlaceholder: "Qué tiene la factura",
  notWaiting: (where: string) =>
    `Todavía no le toca a un digitador (${where}): solo lectura.`,
  release: "Soltar",
  addLine: "Agregar línea",
  removeLine: "Quitar línea",
  removeLineHint: `Quitar línea (${keysLabel(KEYS.removeLine)} con el foco en ella)`,
  lineRemoved: (line: number, description: string) =>
    `Línea ${String(line)} quitada: ${description}.`,
  cannotRemove: (line: number) =>
    `La línea ${String(line)} viene de la lectura: no se quita.`,
  noLineFocused: "Pon el foco en una línea agregada a mano para quitarla.",
  newLine: "Línea nueva",
  newLineHint:
    "Escribe la descripción como está en la factura y pulsa Enter. Esc la descarta.",
  newLineDescription: "Línea nueva: descripción",
  newLinePlaceholder: "La descripción, como está en la factura",
  lineAdded: (line: number, description: string) =>
    `Línea ${String(line)} agregada: ${description}. Falta su cantidad.`,
  released: (id: string) =>
    `Factura ${id}: soltada, ya la puede tomar otra persona.`,
  missing: (what: string) => `Para guardarlo como Válido falta ${what}.`,
  needQuantity: (line: number) =>
    `Falta la cantidad de la línea ${String(line)}.`,
  quantityNotPositive: (line: number) =>
    `La cantidad de la línea ${String(line)} tiene que ser mayor que 0.`,
  noLinesWithData:
    "No hay ninguna línea con datos. Si es así, es Inválido o Para información.",
  saved: (id: string, decision: string) => `Factura ${id}: ${decision}.`,
  changed: (count: number) =>
    count === 0
      ? "Sin correcciones."
      : count === 1
        ? "1 dato corregido."
        : `${String(count)} datos corregidos.`,
  fullscreen: "Pantalla completa",
  exitFullscreen: "Salir de pantalla completa",
  fullWidth: "Ancho completo",
  exitFullWidth: "Ver la cola",
  closeDocument: "Cerrar la factura",
  savingOne: (id: string) => `Guardando la factura ${id}…`,
  waitingClaim: "Esperando a que esta factura sea tuya: un momento.",
  takenByOther: (id: string) => `El ${id} lo tomó otra persona.`,
  screenExits: "Salir de la pantalla completa",
  previousDocument: "Factura anterior",
  nextDocument: "Factura siguiente",
  backToWork: "Volver a Work ▸ Facturas",
  unsavedTitle: "Tienes cambios sin guardar",
  unsavedBody:
    "¿Salir igual? Lo que escribiste sin guardar se pierde. Enter te deja aquí.",
  unsavedStay: "Quedarme",
  unsavedLeave: "Salir sin guardar",
  palette: "Menú de comandos",
  paletteSearch: "Escribe un comando o el número de una factura…",
  paletteEmpty: "Nada coincide.",
  shortcuts: "Atajos de teclado",
  original: "La foto tal como llegó",
  originalPdfTitle: "El PDF tal como llegó",
  originalPdf:
    "El archivo que subió el participante. En los recortes se ve dibujado como imagen.",
  /* ── Deshacer y rehacer ── */
  /** Lo retenido: todavía no salió. */
  held: (id: string, label: string) =>
    `Factura ${id}: ${label}. Se guarda en unos segundos.`,
  /** Abierto desde la cola mientras espera: bloqueado, con «Deshacer». */
  heldLocked: (label: string) => `${label}: se guarda en unos segundos`,
  heldSending: (label: string) => `${label}: guardándose`,
  undone: (id: string) =>
    `Factura ${id}: deshecha. Sigue sin decidir, con lo que habías escrito.`,
  redone: (id: string, label: string) =>
    `Factura ${id}: ${label} otra vez. Se guarda en unos segundos.`,
  heldFailed: (id: string, why: string) =>
    `La decisión de la factura ${id} no se guardó: ${why}`,
  heldOffline: (id: string) =>
    `Sin conexión: la decisión de la factura ${id} se guardará al volver.`,
  recovered: (ids: readonly string[]) =>
    ids.length === 1
      ? `Se guardó la decisión pendiente de la factura ${ids[0] ?? ""}.`
      : `Se guardaron las decisiones pendientes de las facturas ${ids.join(", ")}.`,
  recoveredOld: (count: number) =>
    count === 1
      ? "Una decisión pendiente era de hace más de 15 minutos y se descartó: esa factura sigue sin decidir."
      : `${String(count)} decisiones pendientes eran de hace más de 15 minutos y se descartaron: esas facturas siguen sin decidir.`,
  nothingToUndo: "Nada que deshacer.",
  nothingToRedo: "Nada que rehacer.",
  redoStale:
    "Cambiaste la factura después de deshacer: guárdala con lo nuevo, ya no se rehace.",
  releaseFailed: (id: string) =>
    `No se pudo soltar la factura ${id}: queda tomada por ti unos minutos.`,
  lineUndone: (line: number) => `Línea ${String(line)} quitada otra vez.`,
  lineBack: (line: number, description: string) =>
    `Línea ${String(line)} de vuelta: ${description}.`,
  undo: "Deshacer",
  redo: "Rehacer",
  openFailed: (id: string) => `Abrir ${id}`,
  undoTitle: (what: string, seconds: number) =>
    `Deshacer: ${what} (se guarda en ${String(seconds)} s)`,
  redoTitle: (what: string, seconds: number) =>
    `Rehacer: ${what} (${String(seconds)} s para rehacer)`,
  openFailedTitle: (id: string) =>
    `Abrir la factura ${id} para corregir su decisión`,
  undoWhat: {
    decide: (id: string, label: string) => `Factura ${id}, ${label}`,
    addLine: (line: number) => `agregar la línea ${String(line)}`,
    removeLine: (line: number) => `quitar la línea ${String(line)}`,
    setProduct: (line: number) =>
      `cambiar el producto de la línea ${String(line)}`,
  },
  heldInQueue: "Se guarda en unos segundos: se puede deshacer",
  noPermission:
    "No tienes permiso para ver esta sección. Si crees que deberías, pídeselo a quien administra el programa.",
  mockBadge: "Datos de prueba",
  mock: "Con datos de prueba: el API de la digitación todavía no está. Lo que se guarda vive en esta pestaña.",
} as const;

/**
 * Los nombres de pantalla de los campos que nombra un `fieldErrors` del API
 * (`field-errors.ts`).
 */
export const FIELD_ERROR_LABELS: Partial<Record<string, string>> = {
  issuedAt: "Fecha",
  documentNumber: "Número",
  lines: "Líneas",
  product: "producto",
  description: "descripción",
  quantity: "cantidad",
  reasonKey: "Motivo",
  note: "Nota",
};

/** La línea que nombra un `fieldErrors` (`lines.N`), por su lugar. */
export const fieldErrorLine = (line: number): string => `Línea ${String(line)}`;

/** Lo que falta, dicho como se dice. */
export const MISSING_LABELS = {
  number: "el número",
  date: "la fecha",
} as const;
